import * as React from 'react'
import { useRef } from 'react'
import SwiperCore, { Navigation } from 'swiper/core'
import { Swiper, SwiperSlide } from 'swiper/react'
import { cn } from '../utils/addToClassName'
import { translate } from '../utils/translate'
import { SVGIcon } from './svg'

// Register Swiper with Navigation module
SwiperCore.use([Navigation])

export type CarouselProps = {
  items: React.ReactNode[]
  arrowsRef?: {
    prev: React.RefObject<HTMLButtonElement>
    next: React.RefObject<HTMLButtonElement>
  }
}

export const CarouselLayout = ({ items, arrowsRef }: CarouselProps) => {
  const prevRef = arrowsRef ? arrowsRef.prev : useRef<HTMLButtonElement>(null)
  const nextRef = arrowsRef ? arrowsRef.next : useRef<HTMLButtonElement>(null)

  return (
    <>
      {!arrowsRef && <ArrowButtons prevRef={prevRef} nextRef={nextRef} />}

      <Swiper
        className="carousel-wrapper"
        spaceBetween={24}
        slidesPerView={1.5}
        navigation={{
          prevEl: prevRef.current,
          nextEl: nextRef.current
        }}
        onInit={(swiper) => {
          if (swiper.params.navigation && typeof swiper.params.navigation != 'boolean') {
            swiper.params.navigation.prevEl = prevRef.current
            swiper.params.navigation.nextEl = nextRef.current
            swiper.navigation.init()
            swiper.navigation.update()
          }
        }}
        breakpoints={{
          // when window width is >= 768px
          768: { slidesPerView: 2.5 },
          // when window width is >= 1024px
          1024: { slidesPerView: items.length > 4 ? 3.5 : 2.5 },
          // when window width is >= 1680px
          1680: { slidesPerView: items.length > 4 ? 4.5 : 3.5 }
        }}
        lazy
        loop
        centeredSlides
      >
        {items.map((item, index) => (
          <SwiperSlide key={index}>{item}</SwiperSlide>
        ))}
      </Swiper>
    </>
  )
}

export const ArrowButtons = ({
  className,
  prevRef,
  nextRef
}: {
  className?: string
  prevRef: React.RefObject<HTMLButtonElement>
  nextRef: React.RefObject<HTMLButtonElement>
}) => {
  return (
    <div className={cn('carousel-arrows', className)}>
      <ArrowButton kind="prev" ref={prevRef} />
      <ArrowButton kind="next" ref={nextRef} />
    </div>
  )
}

const ArrowButton = React.forwardRef<HTMLButtonElement, { kind: 'prev' | 'next' }>(({ kind }, ref) => {
  const label =
    kind == 'prev'
      ? translate('Common:Pagination:Previous')
      : translate('Common:Pagination:Next')
  return (
    <button ref={ref} className={`carousel-arrow carousel-arrow--${kind}`} title={label}>
      <span className="sr-text">{label}</span>
      <SVGIcon id={kind == 'prev' ? 'chevron-left' : 'chevron-right'} size={30} />
    </button>
  )
})
